import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Add, ChevronLeft, ChevronRight, NoteAltOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getPanelRole } from '../../../../services/api';
import {
    createClinicalNote,
    fetchClinicalNote,
    getPatientNotes
} from '../../../../services/clinicalNotes';
import { useRole } from '../../../../utils/RoleContext';
import OverviewCard from './OverviewCard';

const NotesOverviewCard = ({ patientUuid }) => {
    const { t } = useTranslation();
    const { role } = useRole();
    const queryClient = useQueryClient();
    const scope = getPanelRole();
    const allowed = ['DOCTOR', 'CLINIC_CARE_GIVER'].includes(role);
    const [tokens, setTokens] = useState([null]);
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState(null);
    const [creating, setCreating] = useState(false);
    const [content, setContent] = useState('');
    const label = (key, options) => t(`userPage.overview.clinicalNotes.${key}`, options);
    const list = useQuery({
        queryKey: ['clinical-notes', scope, patientUuid, tokens[page]],
        queryFn: ({ signal }) => getPatientNotes(patientUuid, tokens[page], signal),
        enabled: allowed && Boolean(patientUuid),
        staleTime: 60000
    });
    const notes = Array.isArray(list.data?.notes) ? list.data.notes : [];
    const details = useQueries({
        queries: notes.map((note) => ({
            queryKey: ['clinical-note', scope, patientUuid, note.uuid],
            queryFn: ({ signal }) => fetchClinicalNote(note.uuid, signal),
            staleTime: 60000
        }))
    });
    const create = useMutation({
        mutationFn: createClinicalNote,
        onSuccess: async () => {
            setCreating(false);
            setContent('');
            setPage(0);
            setTokens([null]);
            await queryClient.invalidateQueries({
                queryKey: ['clinical-notes', scope, patientUuid]
            });
        }
    });
    const selectedDetail = details[notes.findIndex((note) => note.uuid === selected)];
    // The API returns a pagination token even on the final page.
    const hasNextPage =
        Boolean(list.data?.nextToken) && (page + 1) * 3 < list.data?.count;
    const closeCreate = () => {
        if (create.isPending) return;
        setCreating(false);
        setContent('');
        create.reset();
    };
    const nextPage = () => {
        if (!hasNextPage) return;
        setTokens((previous) => [...previous.slice(0, page + 1), list.data.nextToken]);
        setPage((previous) => previous + 1);
    };
    const date = (value) =>
        dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY, HH:mm') : '-';

    return (
        <OverviewCard
            title={t('userPage.overview.notes')}
            icon={<NoteAltOutlined fontSize="small" />}
            height={310}
            minHeight={310}
            contentInset={4}
            action={
                allowed && (
                    <Tooltip title={label('add')}>
                        <IconButton
                            aria-label={label('add')}
                            color="primary"
                            onClick={() => {
                                create.reset();
                                setCreating(true);
                            }}
                        >
                            <Add />
                        </IconButton>
                    </Tooltip>
                )
            }
        >
            {!allowed ? (
                <Typography color="text.secondary">{label('unavailable')}</Typography>
            ) : list.isPending ? (
                [1, 2, 3].map((item) => <Skeleton key={item} height={58} />)
            ) : list.isError ? (
                <Box>
                    <Alert severity="error">{label('loadError')}</Alert>
                    <Button onClick={() => list.refetch()}>{label('retry')}</Button>
                </Box>
            ) : notes.length === 0 ? (
                <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
                    <Typography color="text.secondary">
                        {t('userPage.overview.noNotes')}
                    </Typography>
                </Box>
            ) : (
                <>
                    {notes.map((note, index) => (
                        <ButtonBase
                            key={note.uuid}
                            onClick={() => setSelected(note.uuid)}
                            sx={{
                                width: '100%',
                                textAlign: 'left',
                                display: 'block',
                                height: 58,
                                flexShrink: 0,
                                py: 1,
                                borderBottom: '1px solid #E0E5E9'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 1
                                }}
                            >
                                <Typography variant="body2" fontWeight={700} noWrap>
                                    {note.author?.name || '-'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                    {date(note.ts)}
                                </Typography>
                            </Box>
                            {details[index]?.isPending ? (
                                <Skeleton width="85%" />
                            ) : (
                                <Typography variant="body2" noWrap color="text.secondary">
                                    {details[index]?.isError
                                        ? label('detailError')
                                        : details[index]?.data?.format === 'HTML'
                                          ? label('openNote')
                                          : details[index]?.data?.content}
                                </Typography>
                            )}
                        </ButtonBase>
                    ))}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mt: 'auto',
                            mx: -4,
                            pt: 1
                        }}
                    >
                        <IconButton
                            size="small"
                            aria-label={label('previous')}
                            disabled={page === 0}
                            onClick={() => setPage((previous) => previous - 1)}
                        >
                            <ChevronLeft />
                        </IconButton>
                        <Typography variant="caption">
                            {label('page', {
                                current: page + 1,
                                total: Math.max(
                                    page + 1,
                                    Math.ceil((list.data?.count || notes.length) / 3)
                                )
                            })}
                        </Typography>
                        <IconButton
                            size="small"
                            aria-label={label('next')}
                            disabled={!hasNextPage}
                            onClick={nextPage}
                        >
                            <ChevronRight />
                        </IconButton>
                    </Box>
                </>
            )}
            <Dialog
                open={Boolean(selected)}
                onClose={() => setSelected(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{label('note')}</DialogTitle>
                <DialogContent>
                    {selectedDetail?.isPending ? (
                        <Skeleton height={150} />
                    ) : selectedDetail?.isError ? (
                        <Box>
                            <Alert severity="error">{label('detailError')}</Alert>
                            <Button onClick={() => selectedDetail.refetch()}>
                                {label('retry')}
                            </Button>
                        </Box>
                    ) : (
                        selectedDetail?.data && (
                            <>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 2 }}
                                >
                                    {selectedDetail.data.author?.name} -{' '}
                                    {date(selectedDetail.data.ts)}
                                </Typography>
                                <Box sx={{ overflowWrap: 'anywhere' }}>
                                    {selectedDetail.data.format === 'MARKDOWN' ? (
                                        <ReactMarkdown>
                                            {selectedDetail.data.content || ''}
                                        </ReactMarkdown>
                                    ) : selectedDetail.data.format === 'HTML' ? (
                                        <iframe
                                            title={label('note')}
                                            sandbox=""
                                            srcDoc={selectedDetail.data.content || ''}
                                            style={{
                                                width: '100%',
                                                height: 320,
                                                border: 0
                                            }}
                                        />
                                    ) : (
                                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedDetail.data.content}
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelected(null)}>{label('close')}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={creating} onClose={closeCreate} fullWidth maxWidth="sm">
                <DialogTitle>{label('add')}</DialogTitle>
                <DialogContent>
                    {create.isError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {label('saveError')}
                        </Alert>
                    )}
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={6}
                        label={label('content')}
                        value={content}
                        disabled={create.isPending}
                        onChange={(event) => setContent(event.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button disabled={create.isPending} onClick={closeCreate}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={create.isPending || !content.trim()}
                        onClick={() =>
                            create.mutate({
                                patient: patientUuid,
                                content: content.trim()
                            })
                        }
                    >
                        {create.isPending ? label('saving') : t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </OverviewCard>
    );
};

NotesOverviewCard.propTypes = { patientUuid: PropTypes.string.isRequired };

export default NotesOverviewCard;
